import { useResource } from "@/hooks/useResource";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { LoadingState } from "@/components/common/States";
import { Badge } from "@/components/ui/badge";
import { fmtCurrency, fmtNumber } from "@/lib/format";
import { AlertTriangle } from "lucide-react";

const money = (v) => (v == null ? "—" : fmtCurrency(v));

export function SellingPriceDetails({ product, open, onOpenChange }) {
  const { data: d, isLoading } = useResource(open && product ? `/reports/product-price-insights/${product.productId}` : null);
  const o = d?.overall;
  const unit = d?.unit || product?.productUnit || "pcs";
  const marginComparable = product && unit === (product.yieldUnit || product.productUnit || unit);
  const cost = Number(product?.decidedCost || product?.currentUnitCost || 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[92vh] overflow-y-auto bg-white sm:max-w-4xl" data-testid="price-details-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Selling Price Details — {product?.name}</DialogTitle>
          <DialogDescription className="text-xs">
            {product?.productId}{product?.sku ? ` · SKU ${product.sku}` : ""} · Derived from actual eligible sales invoices
            (CONFIRMED/BOOKED incl. Tally imports; draft/cancelled/rejected excluded). Historical actuals — configured
            Buyer Price Master prices are shown separately and are never overwritten.
          </DialogDescription>
        </DialogHeader>

        {isLoading || !d ? <LoadingState label="Analysing invoices…" /> : !o ? (
          <div className="rounded-lg border border-stone-200 bg-stone-50 p-6 text-center text-sm text-stone-500" data-testid="price-details-none">
            No invoice price available
            {d.configuredPrices?.length > 0 && (
              <div className="mt-3 text-left">
                <div className="mb-1 text-xs font-bold uppercase tracking-wide text-stone-500">Configured buyer prices (Buyer Price Master)</div>
                {d.configuredPrices.map((c, i) => (
                  <div key={i} className="text-xs text-stone-700">{c.buyerName}: <b>{money(c.price)}</b>/{c.unit || unit} (effective {c.effectiveDate || "—"})</div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
              {[["Weighted Avg", `${money(o.weightedAvg)}/${unit}`, "overall-avg"],
                ["Latest", `${money(o.latest?.rate)}/${unit}`, "latest"],
                ["Min", money(o.minRate), "min"], ["Max", money(o.maxRate), "max"],
                ["Invoice Lines", o.lineCount, "lines"], ["Latest Invoice", o.latest?.invoiceNo || "—", "latest-inv"]].map(([k, v, tid]) => (
                <div key={k} className="rounded-lg border border-stone-200 bg-stone-50 px-2.5 py-2" data-testid={`pd-kpi-${tid}`}>
                  <div className="text-sm font-bold text-stone-900">{v}</div>
                  <div className="text-[9px] font-semibold uppercase tracking-wide text-stone-500">{k}</div>
                </div>
              ))}
            </div>
            <div className="text-[11px] text-stone-500">
              Latest: invoice <b>{o.latest?.invoiceNo}</b> dated {o.latest?.date} · {o.latest?.buyerName} · source{" "}
              <Badge variant="outline" className="text-[9px]">{o.latest?.source}</Badge> ·
              basis: Σ taxable value {money(o.totalValue)} ÷ Σ qty {fmtNumber(o.totalQty)} {unit} = <b>{money(o.weightedAvg)}/{unit}</b>
              {marginComparable && cost > 0 && o.weightedAvg ? (
                <span data-testid="pd-margin"> · margin vs cost: <b className={o.weightedAvg >= cost ? "text-emerald-700" : "text-rose-700"}>{money(o.weightedAvg - cost)}/{unit} ({(((o.weightedAvg - cost) / o.weightedAvg) * 100).toFixed(1)}%)</b></span>
              ) : null}
            </div>

            <table className="w-full text-xs" data-testid="pd-buyers-table">
              <thead>
                <tr className="border-y border-stone-200 bg-stone-50 text-left text-[10px] font-bold uppercase tracking-wide text-stone-500">
                  {["Buyer", "Weighted Avg", "Latest", "Min", "Max", "Lines", "Last Invoice", "Source", "Configured Price", "Diff (avg vs configured)"].map((h) => <th key={h} className="px-2 py-2">{h}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {d.buyers.map((b) => (
                  <tr key={b.buyerId} data-testid={`pd-buyer-${b.buyerId}`}>
                    <td className="px-2 py-1.5 font-medium text-stone-900">{b.buyerName}</td>
                    <td className="px-2 py-1.5 font-semibold tabular-nums">{money(b.weightedAvg)}/{unit}</td>
                    <td className="px-2 py-1.5 tabular-nums">{money(b.latest?.rate)}</td>
                    <td className="px-2 py-1.5 tabular-nums">{money(b.minRate)}</td>
                    <td className="px-2 py-1.5 tabular-nums">{money(b.maxRate)}</td>
                    <td className="px-2 py-1.5 tabular-nums">{b.lineCount}</td>
                    <td className="px-2 py-1.5">{b.latest?.invoiceNo}<span className="text-stone-400"> · {b.latest?.date}</span></td>
                    <td className="px-2 py-1.5"><Badge variant="outline" className="text-[9px]">{b.latest?.source}</Badge></td>
                    <td className="px-2 py-1.5 tabular-nums">{b.configuredPrice != null ? `${money(b.configuredPrice)}/${b.configuredUnit || unit}` : "—"}</td>
                    <td className={`px-2 py-1.5 tabular-nums font-semibold ${b.diffVsConfigured == null ? "" : b.diffVsConfigured > 0 ? "text-emerald-700" : b.diffVsConfigured < 0 ? "text-rose-700" : ""}`}>
                      {b.diffVsConfigured != null ? `${b.diffVsConfigured > 0 ? "+" : ""}${money(b.diffVsConfigured)} (${b.diffVsConfiguredPct > 0 ? "+" : ""}${b.diffVsConfiguredPct}%)` : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {Object.keys(d.excludedLines || {}).length > 0 && (
              <div data-testid="pd-excluded" className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <div>Excluded invoice lines (not averaged): {Object.entries(d.excludedLines).map(([r, n]) => `${n}× ${r}`).join(" · ")}</div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
